import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useParams } from 'react-router-dom';
// components
import { useSettingsContext } from 'src/components/settings';
import { RoleBasedGuard } from 'src/auth/guard';
//
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import FloatingChatWidget from './floating-chat-widget';
import { OnboardingProvider } from 'src/sections/onboarding/onboarding-context';
import WelcomeModal from 'src/sections/onboarding/welcome-modal';
import TourRunner from 'src/sections/onboarding/tour-engine';
import { useDispatch } from 'react-redux';
// redux for access
import { resetAccessRights, startPermissionCheck } from 'src/redux/slices/access';
//permissions
import {permissions} from 'src/assets/data/permissions';
import { getPermissions } from 'src/redux/slices/access';
// ----------------------------------------------------------------------
const buildPathName = (params, path)=>{
  const queryP = Object.keys(params);
  for(var q=0; q<queryP.length;q++){
    path = path.replace(`:${queryP[q]}`,params[queryP[q]])
  }
  return path;
}
export default function DashboardLayout({ children }) {
  return (
    <OnboardingProvider>
      <DashboardLayoutContent>{children}</DashboardLayoutContent>
      <WelcomeModal />
      <TourRunner />
    </OnboardingProvider>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};

// The Guide system (§7) needs context available wherever Header/Main render
// (all 3 layout variants below), so DashboardLayout wraps this content in an
// OnboardingProvider rather than each variant managing its own.
function DashboardLayoutContent({ children }) {

  const location = useLocation();
  const params = useParams();

  const dispatch = useDispatch();

  const filter = useMemo(() =>
    permissions.filter(p => buildPathName(params, p.path) === location.pathname),
    [params, location.pathname]
  );

  // Only re-check permissions when the actual permission name/type changes,
  // not when just the route params change (e.g. switching chat IDs)
  const permissionKey = useMemo(() => {
    if (filter.length > 0) {
      const { permissionType, permissionName } = filter[0];
      return `${permissionName}:${permissionType}`;
    }
    return null;
  }, [filter]);

  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  useEffect(() => {
    if (permissionKey) {
      const [permissionName, permissionType] = permissionKey.split(':');
      // Show loading while permission check is in progress
      dispatch(startPermissionCheck());
      dispatch(getPermissions(permissionName, permissionType));
    } else {
      // Page doesn't require special permissions - allow access
      dispatch(resetAccessRights());
    }
  }, [permissionKey, dispatch])
  
  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>
          <RoleBasedGuard>
            {children}
          </RoleBasedGuard>
        </Main>
        <FloatingChatWidget />
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            display: { lg: 'flex' },
            minHeight: { lg: 1 },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>
            <RoleBasedGuard>
              {children}
            </RoleBasedGuard>
          </Main>
        </Box>
        <FloatingChatWidget />
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          display: { lg: 'flex' },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}

        <Main>
          <RoleBasedGuard>
            {children}
          </RoleBasedGuard>
        </Main>
      </Box>
      <FloatingChatWidget />
    </>
  );
}

DashboardLayoutContent.propTypes = {
  children: PropTypes.node,
};
